import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import { breakpoint, duration } from '@twistopayments/design-system/styles'

import b2bDoubleArrow from './b2b-double-arrow.svg'
import darkDoubleArrow from './dark-double-arrow.svg'
import doubleArrow from './double-arrow.svg'
import paramDoubleArrow from './param-double-arrow.svg'

export const THUMB_WIDTH = 28
const THUMB_HEIGHT = 24
const SLIDER_LINE_HEIGHT = 8

type SvgAsset = string | { src: string }

const getSvgSource = (asset: SvgAsset) =>
  typeof asset === 'string' ? asset : asset.src

const arrowImage: Record<Theme['themeName'], SvgAsset> = {
  b2b: b2bDoubleArrow,
  b2c: paramDoubleArrow,
  param: paramDoubleArrow,
  twisto: doubleArrow,
}

const getSliderIcon = (theme: Theme) =>
  getSvgSource(
    theme.mode === 'dark'
      ? darkDoubleArrow
      : arrowImage[theme.presentation.range.thumbArtwork] ?? paramDoubleArrow
  )

const sliderThumbStyle = (theme: Theme) => `
  appearance: none;
  width: ${THUMB_WIDTH}px;
  height: ${THUMB_HEIGHT}px;
  background: no-repeat ${theme.palette.background.main} url("${getSliderIcon(
    theme
  )}") center;
  border: 1px solid ${theme.presentation.selection.strong};
  border-radius: ${theme.spacing[4]};
  box-shadow: ${theme.presentation.range.shadow};
  transition: border ${duration.shortest}ms ease-out,
    box-shadow ${duration.shortest}ms ease-out;
`

const hoverRuleSet = (theme: Theme) => `
  border-color: ${theme.presentation.selection.strong};
  box-shadow: ${theme.presentation.range.interactionShadow};
`

const activeRuleSet = (theme: Theme) => `
  border-color: ${theme.presentation.selection.active};
  box-shadow: ${theme.presentation.range.interactionShadow};
`

const backgroundStyle = (theme: Theme) => `
  background: ${theme.presentation.selection.active};
  background-repeat: no-repeat;
  border-radius: ${theme.spacing[4]};
`

export const wrapper = css`
  position: relative;
  width: 100%;
`

export const container = (theme: Theme) => css`
  width: 100%;
  ${theme.presentation.range.frame}
`

export const slider = (theme: Theme) => css`
  /*style declaration for IE, Edge */
  display: block;
  padding: 0;
  margin: 0;
  cursor: pointer;

  height: 70px;
  width: 100%;

  /* style for rest browsers */
  @supports not (-ms-user-select: none) {
    appearance: none;
    height: ${SLIDER_LINE_HEIGHT}px;
    margin: ${theme.presentation.range.margin};

    ${backgroundStyle(theme)}
    background: ${theme.presentation.range.unfilled};
    background-repeat: no-repeat;
    background-image: linear-gradient(
      0deg,
      ${theme.presentation.selection.active} 0%,
      ${theme.presentation.selection.active} 100%
    );
  }

  &::-webkit-slider-thumb {
    ${sliderThumbStyle(theme)}
  }

  &::-moz-range-thumb {
    ${sliderThumbStyle(theme)}
  }

  &::-moz-focus-outer {
    border: 0;
  }

  &::-ms-thumb {
    ${sliderThumbStyle(theme)}
  }

  &::-ms-fill-lower {
    background: ${theme.presentation.selection.active};
  }

  &::-ms-fill-upper {
    background: ${theme.presentation.selection.track};
  }

  &::-ms-track {
    color: rgba(0, 0, 0, 0);
    height: ${theme.spacing[8]};
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: ${theme.spacing[4]};
  }

  &::-ms-tooltip {
    display: none;
  }

  &:hover::-webkit-slider-thumb {
    ${hoverRuleSet(theme)}
  }
  &:hover::-moz-range-thumb {
    ${hoverRuleSet(theme)}
  }
  &:hover::-ms-thumb {
    ${hoverRuleSet(theme)}
  }
  &:focus-visible::-webkit-slider-thumb,
  &:active::-webkit-slider-thumb {
    ${activeRuleSet(theme)}
  }
  &:focus-visible::-moz-range-thumb,
  &:active::-moz-range-thumb {
    ${activeRuleSet(theme)}
  }
  &:focus-visible::-ms-thumb,
  &:active::-ms-thumb {
    ${activeRuleSet(theme)}
  }
`

export const rangeSlider = css`
  position: relative;
  width: 100%;
  height: ${SLIDER_LINE_HEIGHT}px;
  box-sizing: content-box;
  cursor: pointer;
  user-select: none;
  /* hack for disabling highlight on mobile */
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0) !important;
`

export const rail = (theme: Theme) => css`
  width: 100%;
  height: 100%;
  background: ${theme.presentation.selection.track};
  border-radius: ${theme.spacing[4]};
`

export const track = (theme: Theme) => css`
  height: ${SLIDER_LINE_HEIGHT}px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${backgroundStyle(theme)};
`

export const sliderThumb = (theme: Theme) => css`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);

  ${sliderThumbStyle(theme)}

  &:hover {
    ${hoverRuleSet(theme)}
  }

  &:focus-visible,
  &:active {
    ${activeRuleSet(theme)}
  }
`

export const footer = (theme: Theme) => css`
  margin-top: ${theme.presentation.range.footerGap};
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
`

export const label = (theme: Theme) => css`
  ${theme.presentation.range.label.mobile}
  ${breakpoint.up('sm')} {
    ${theme.presentation.range.label.desktop}
  }
`
